// The forecast strip's arithmetic, shared by the Forecast block and the
// forecast inside Trail conditions on both templates, so the two can
// offer the same settings and cannot disagree about what "now", "3 PM"
// or "Tomorrow" means. No imports, so the harness can run it in node.

export const FORECAST_MODES = ['hourly', 'daily'];

export function forecastMode(mode) {
    return mode === 'daily' ? 'daily' : 'hourly';
}

export function forecastDays(n) {
    return Math.max(1, Math.min(7, Number(n) || 5));
}

// Hourly: now plus the next couple of hours. Daily: the server's
// per-day aggregate. Either way a slot has `when` and a reading.
export function forecastSlots(forecast, mode, dailyCount) {
    const data = forecast || {};
    if (forecastMode(mode) === 'daily') {
        const days = Array.isArray(data.daily) ? data.daily : [];
        const todayDate = data.today_date || null;
        // Tomorrow is derived from the server's own today, so the
        // comparison happens in the organization's day rather than
        // the reader's.
        let tomorrowDate = null;
        if (todayDate) {
            const [y, m, d] = String(todayDate).split('-').map(Number);
            const t = new Date(Date.UTC(y, m - 1, d + 1));
            tomorrowDate = `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
        }
        return days.slice(0, forecastDays(dailyCount)).map((d) => {
            let when;
            if (d.date === todayDate) when = 'Today';
            else if (d.date === tomorrowDate) when = 'Tomorrow';
            else {
                // A noon UTC anchor read back in UTC: a calendar day put
                // through the reader's zone renders as the day before
                // west of Greenwich.
                const [yy, mm, dd] = String(d.date).split('-').map(Number);
                when = new Date(Date.UTC(yy, mm - 1, dd, 12)).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
            }
            return { when, code: d.code, temp_c: null, high_c: d.high_c, low_c: d.low_c, summary: '' };
        });
    }
    if (!data.current) return [];
    const c = data.current;
    const out = [{ when: 'Now', code: c.code, temp_c: c.temp_c, high_c: null, low_c: null, summary: c.summary || '' }];
    for (const u of (data.upcoming || [])) {
        // An absolute clock time. "+Nh" rounded two rows into the same
        // bucket whenever both were under about 90 minutes away.
        const at = new Date(u.at);
        const when = Number.isNaN(at.getTime()) ? '' : at.toLocaleTimeString([], { hour: 'numeric' });
        out.push({ when, code: u.code, temp_c: u.temp_c, high_c: null, low_c: null, summary: u.summary || '' });
    }
    return out.slice(0, 3);
}

export function forecastTemp(temp_c, unitSystem) {
    if (temp_c == null) return '—';
    if (unitSystem === 'imperial') return `${Math.round((temp_c * 9 / 5) + 32)}°`;
    return `${Math.round(temp_c)}°`;
}

// WMO weather code → emoji. Explicit ranges, so rain showers (80-82)
// are not bucketed as snow and snow (71-77) stays snow.
export function forecastEmoji(code) {
    if (code == null) return '🌡';
    if (code === 0) return '☀️';
    if (code <= 3) return '⛅';
    if (code === 45 || code === 48) return '🌫';
    if (code >= 51 && code <= 57) return '🌦';
    if (code >= 61 && code <= 67) return '🌧';
    if (code >= 71 && code <= 77) return '❄️';
    if (code >= 80 && code <= 82) return '🌧';
    if (code === 85 || code === 86) return '🌨';
    if (code >= 95 && code <= 99) return '⛈';
    return '🌡';
}

// The Trail conditions block's own forecast settings. Same four
// settings as the Forecast block (mode, days, heading, subtitle), under
// forecast_* keys, plus where it sits and what it sits on.
export function conditionsForecast(config) {
    const c = config || {};
    return {
        show: Boolean(c.show_forecast),
        mode: forecastMode(c.forecast_mode),
        days: forecastDays(c.forecast_days),
        heading: String(c.forecast_heading || '').trim(),
        subtitle: String(c.forecast_subtitle || '').trim(),
        position: c.forecast_position === 'top' ? 'top' : 'bottom',
        panel: c.forecast_panel === 'card' ? 'card' : 'transparent',
        network: c.forecast_network_id ? String(c.forecast_network_id) : '',
    };
}

// Which network's forecast a block draws. Automatic (no id) is the
// network with the most weather, as before. A chosen network with no
// forecast yet draws nothing rather than another network's weather
// under its name.
export function forecastFor(siteData, networkId) {
    const id = networkId == null || networkId === '' ? null : String(networkId);
    if (!id) return siteData?.forecast || {};
    return siteData?.forecast_networks?.[id] || {};
}

// The networks a forecast can be pinned to: public networks, each
// saying whether it has a forecast yet.
export function forecastNetworkOptions(siteData) {
    const have = siteData?.forecast_networks || {};
    return (siteData?.networks || []).map((n) => ({
        id: String(n.id),
        name: n.name,
        hasForecast: !!have[String(n.id)]?.current,
    }));
}

// WMO weather code → a few words, for a forecast that prints the day's
// weather rather than drawing it.
export function forecastWords(code) {
    if (code == null) return '';
    if (code === 0) return 'Clear';
    if (code === 1) return 'Mostly clear';
    if (code === 2) return 'Partly cloudy';
    if (code === 3) return 'Cloudy';
    if (code === 45 || code === 48) return 'Fog';
    if (code >= 51 && code <= 57) return 'Drizzle';
    if (code >= 61 && code <= 67) return 'Rain';
    if (code >= 71 && code <= 77) return 'Snow';
    if (code >= 80 && code <= 82) return 'Showers';
    if (code === 85 || code === 86) return 'Snow showers';
    if (code >= 95 && code <= 99) return 'Thunderstorms';
    return '';
}
